import React from 'react';

import { T, TF } from 'react-translator-component';
import styled from 'styled-components';

import {
  ISSUE_ORDER,
  issueConsequence,
  issueLabel,
  issueSeverity,
  SEVERITY_STYLE,
} from './stationIssues';


import {
  IntegrationStationTotals,
  StationIssue,
} from '../../../../types/responses/Interoperability';
import { StatusDot } from '../../../atoms/StatusBadge';
import { CollapsibleSection } from '../../../molecules/CollapsibleSection';
import { formatCount } from '../transactionFormat';


const Row = styled.button<{ selected: boolean }>`
  width: 100%;
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 8px 14px;
  border: none;
  border-top: 1px solid #f7f8f9;
  background: ${({ selected }) => (selected ? '#f4f6fb' : '#fff')};
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #f7f8f9;
  }
`;

const Count = styled.strong`
  font-size: 13px;
  color: #2d3339;
  font-variant-numeric: tabular-nums;
  min-width: 46px;
`;

const What = styled.span`
  font-size: 12.5px;
  color: #2d3339;
  min-width: 130px;
`;

const Why = styled.span`
  flex: 1;
  font-size: 11.5px;
  color: #8a9099;
`;

/**
 * O botao de limpar.
 *
 * So aparece com filtro ligado: botao que nao faz nada e ruido, e um "limpar" sempre
 * visivel sugere que ha sempre algo para limpar.
 */
const Clear = styled.button`
  background: #fff;
  color: #4a5462;
  border: 1px solid #d9dce1;
  border-radius: 6px;
  padding: 3px 10px;
  cursor: pointer;
  font-size: 11.5px;
  white-space: nowrap;
`;

const Clean = styled.p`
  margin: 0;
  padding: 12px 14px;
  font-size: 12.5px;
  color: #1e7a45;
`;

export type StationRegistrationPanelProps = {
  totals: IntegrationStationTotals | null;

  /** Problema em foco no filtro, para a linha aparecer marcada. */
  selectedIssue: StationIssue | '';

  /** Se o filtro está em "só sem problema". */
  onlyCorrect: boolean;

  onSelectIssue: (issue: StationIssue) => void;
  onSelectCorrect: () => void;

  /** Volta a mostrar tudo. Só aparece com algum recorte de status ligado. */
  onClear: () => void;
};

/**
 * A área de status cadastral da aba Estações (MV2-1706).
 *
 * Antes o assunto estava espalhado: dois cartões no cabeçalho, uma opção no filtro e uma
 * frase com link dentro do aviso do mapa. Quem queria ver as estações com problema tinha
 * três caminhos diferentes, e o do mapa misturava informação com ação — foi o que ficou
 * confuso.
 *
 * Agora é um lugar só. Cada linha diz **quantas**, **o que falta** e **o que isso causa no
 * app do parceiro**, e clicar filtra a lista. Os cartões do cabeçalho ficaram com o outro
 * assunto, que é contrato: o que eu compartilho e o que eu recebo.
 *
 * Só aparecem os problemas que existem: linha com zero é ruído, e a ausência de linha já
 * diz que aquele problema não existe neste filtro.
 */
export const StationRegistrationPanel: React.FC<StationRegistrationPanelProps> = ({
  totals,
  selectedIssue,
  onlyCorrect,
  onSelectIssue,
  onSelectCorrect,
  onClear,
}) => {
  const withIssues = totals?.with_issues_count ?? 0;
  const correct = Math.max(0, (totals?.station_count ?? 0) - withIssues);

  /** Algum recorte de status ligado — e o que faz o botao de limpar existir. */
  const filtering = Boolean(selectedIssue) || onlyCorrect;

  const rows = ISSUE_ORDER.map((issue) => ({
    issue,
    count: totals?.by_issue?.[issue] ?? 0,
  })).filter((row) => row.count > 0);

  return (
    <CollapsibleSection
      title={T('Registration status')}
      /*
        Fechada, a seccao diz o que esta filtrando — senao um recorte ligado fica invisivel
        e a lista parece curta sem motivo.
      */
      summary={
        filtering
          ? `${TF('filtering by')} ${selectedIssue ? issueLabel(selectedIssue) : TF('Ready for the partner')}`
          : `${formatCount(withIssues)} ${TF('with problems')}`
      }
      action={
        filtering && (
          <Clear type="button" onClick={onClear}>
            {T('Clear filter')}
          </Clear>
        )
      }
    >
      {rows.length === 0 && (
        <Clean>{T('No registration problem in this filter.')}</Clean>
      )}

      {rows.map((row) => {
        const style = SEVERITY_STYLE[issueSeverity(row.issue)];

        return (
          <Row
            key={row.issue}
            type="button"
            selected={selectedIssue === row.issue}
            onClick={() => onSelectIssue(row.issue)}
          >
            <StatusDot color={style.dot} />
            <Count>{formatCount(row.count)}</Count>
            <What>{issueLabel(row.issue)}</What>
            {/* A consequência fica na mesma linha: é ela que faz alguém corrigir hoje. */}
            <Why>{issueConsequence(row.issue)}</Why>
          </Row>
        );
      })}

      {correct > 0 && (
        <Row type="button" selected={onlyCorrect} onClick={onSelectCorrect}>
          <StatusDot color="#26a35c" />
          <Count>{formatCount(correct)}</Count>
          <What>{T('Ready for the partner')}</What>
          <Why>{T('Nothing missing: the partner can use these.')}</Why>
        </Row>
      )}
    </CollapsibleSection>
  );
};
